import { errorCodeOf } from "@luckymenu/api-client";
import { useMutation, useQuery, useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import { type CancelItemInput, type Order, type OrderType, uuidv7 } from "@luckymenu/core";
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useStore } from "zustand";
import {
	addLine,
	changeQuantity,
	type DraftLine,
	type DraftTarget,
	draftKey,
	draftSummary,
	draftToLines,
	EMPTY_DRAFT,
	type OrderDraft,
	removeLine,
	setLineNote,
	setQuantity,
} from "../ordering/draft";
import { withKotChanges } from "../ordering/kots";
import {
	mergeCandidates,
	moveTargets,
	type OrderView,
	openOrderAt,
	orderView,
} from "../ordering/order-view";
import { entriesFor, type SendEntry, sendsFor } from "../ordering/outbox";
import { type OrderingProblem, orderingProblemOf, takenByOrderOf } from "../ordering/problems";
import { useStaffCore } from "../provider";
import { useKotChanges } from "./use-kots";
import { useLiveBoard } from "./use-live-board";
import { useCan } from "./use-navigation";

/** The restaurant API calls go to: a support session's, or the chosen one. */
export function useRestaurantId(): string | null {
	const core = useStaffCore();
	const support = useStore(core.support, (s) => s.session?.restaurantId ?? null);
	const active = useStore(core.activeRestaurant, (s) => s.restaurantId);
	return support ?? active;
}

/**
 * Changes waiting for the server, from this device: new orders, items for
 * the kitchen, cancels and KOTs done (ordering/outbox.ts). `retry` tries one
 * again at once with the same request ID; `dismiss` gives it up. `loaded`
 * turns true once the entries kept on the device have been read.
 */
export function useOutbox() {
	const { outbox } = useStaffCore();
	const entries = useSyncExternalStore(
		(listener) => outbox.subscribe(listener),
		() => outbox.getEntries(),
		() => outbox.getEntries(),
	);
	return {
		entries,
		loaded: outbox.isLoaded(),
		retry: useCallback((id: string) => outbox.retry(id), [outbox]),
		dismiss: useCallback((id: string) => outbox.dismiss(id), [outbox]),
		/**
		 * A new order refused because the table got an order meanwhile: the
		 * same lines go to that order instead, with the same request ID
		 * (the refused create used none of it).
		 */
		sendToOrder: useCallback(
			(entry: SendEntry, orderId: string) => {
				outbox.replace(entry.id, {
					kind: "addBatch",
					id: entry.id,
					restaurantId: entry.restaurantId,
					orderId,
					input: { orderId, clientRequestId: entry.id, lines: draftToLines(entry.lines) },
					lines: entry.lines,
					tableLabel: entry.tableLabel,
					orderType: entry.orderType,
				});
			},
			[outbox],
		),
	};
}

/**
 * One order for the order screen (OP-07): batches with their items, what
 * this device is still sending to it, and its totals. While a new order is
 * on its way from this device, `status` is `creating` and `creating` holds
 * what was sent. Kept fresh by the socket (`order.updated` refetches it).
 */
export function useOrder(orderId: string) {
	const trpc = useTRPC();
	const canView = useCan("order.view");
	const query = useQuery({
		...trpc.staff.ordering.getOrder.queryOptions({ orderId }),
		enabled: canView,
	});
	const { entries, loaded } = useOutbox();
	const mine = useMemo(() => entriesFor(entries, orderId), [entries, orderId]);
	const creating = sendsFor(mine, orderId).find((e) => e.kind === "createOrder") ?? null;
	const kotChanges = useKotChanges();
	const view: OrderView | null = useMemo(() => {
		if (!query.data) return null;
		const kots = withKotChanges(query.data.kots, kotChanges, new Date().toISOString());
		return orderView({ ...query.data, kots }, mine);
	}, [query.data, mine, kotChanges]);
	return {
		status: view
			? ("ready" as const)
			: creating
				? ("creating" as const)
				: query.isError
					? errorCodeOf(query.error) !== "NOT_FOUND"
						? ("error" as const)
						: // A new order may still be in the entries being read from the device.
							loaded
							? ("notFound" as const)
							: ("loading" as const)
					: ("loading" as const),
		view,
		creating,
		retry: () => void query.refetch(),
	};
}

/**
 * The items picked for an order and not sent yet, shared by the order screen
 * and the item picker, and kept in storage (UX rule 4). Keyed by restaurant
 * and target: an open order, a free table, or a new takeaway.
 */
export function useOrderDraft(target: DraftTarget) {
	const core = useStaffCore();
	const restaurantId = useRestaurantId();
	const key = `${restaurantId ?? "none"}:${draftKey(target)}`;
	const draft = useStore(core.drafts, (s) => s.drafts[key] ?? EMPTY_DRAFT);
	const ready = useStore(core.drafts, (s) => Boolean(s.loaded[key]));
	useEffect(() => {
		void core.drafts.getState().load(key);
	}, [core, key]);
	const change = useCallback(
		(fn: (draft: OrderDraft) => OrderDraft) => core.drafts.getState().change(key, fn),
		[core, key],
	);
	const summary = useMemo(() => draftSummary(draft), [draft]);
	return {
		ready,
		draft,
		/** Units and value before tax. */
		summary,
		add: useCallback((line: DraftLine) => change((d) => addLine(d, line)), [change]),
		increment: useCallback((line: string) => change((d) => changeQuantity(d, line, 1)), [change]),
		decrement: useCallback((line: string) => change((d) => changeQuantity(d, line, -1)), [change]),
		setQuantity: useCallback(
			(line: string, quantity: number) => change((d) => setQuantity(d, line, quantity)),
			[change],
		),
		remove: useCallback((line: string) => change((d) => removeLine(d, line)), [change]),
		setNote: useCallback(
			(line: string, note: string) => change((d) => setLineNote(d, line, note)),
			[change],
		),
		setCovers: useCallback(
			(covers: number | null) =>
				change((d) => ({
					...d,
					covers: covers === null ? null : Math.max(1, Math.min(100, Math.floor(covers))),
				})),
			[change],
		),
		setCustomerName: useCallback(
			(name: string) => change((d) => ({ ...d, customerName: name.slice(0, 60) || null })),
			[change],
		),
		clear: useCallback(() => change(() => EMPTY_DRAFT), [change]),
	};
}

/**
 * "Send to kitchen" (OP-09): takes the draft's lines into the outbox at once
 * and empties the draft. An open order gets a batch (`addBatch`); a free
 * table or a takeaway gets a new order (`createOrder`) with a client-made
 * ID. Returns the order's ID (to open it), or null with nothing to send.
 */
export function useSendToKitchen(
	target: DraftTarget,
	context: { tableLabel: string | null; orderType: OrderType },
) {
	const core = useStaffCore();
	const restaurantId = useRestaurantId();
	const drafts = useOrderDraft(target);
	const allowed = useCan("order.create");
	const { draft, clear } = drafts;
	const send = useCallback((): string | null => {
		if (!allowed || !restaurantId || draft.lines.length === 0) return null;
		const id = uuidv7();
		const lines = draftToLines(draft.lines);
		const common = {
			id,
			restaurantId,
			lines: draft.lines,
			tableLabel: context.tableLabel,
			orderType: context.orderType,
		};
		let orderId: string;
		if (target.kind === "order") {
			orderId = target.orderId;
			core.outbox.add({
				...common,
				kind: "addBatch",
				orderId,
				input: { orderId, clientRequestId: id, lines },
			});
		} else {
			orderId = uuidv7();
			core.outbox.add({
				...common,
				kind: "createOrder",
				orderId,
				input: {
					id: orderId,
					type: target.kind === "table" ? "dine_in" : "takeaway",
					tableId: target.kind === "table" ? target.tableId : null,
					covers: target.kind === "table" ? draft.covers : null,
					customerName: target.kind === "takeaway" ? draft.customerName : null,
					batch: { clientRequestId: id, lines },
				},
			});
		}
		clear();
		return orderId;
	}, [allowed, restaurantId, draft, clear, target, context.tableLabel, context.orderType, core]);
	return { send, canSend: allowed && draft.lines.length > 0 };
}

export type OrderAction = "cancelItem" | "cancelOrder" | "move" | "merge";

export type OrderActionProblem = {
	action: OrderAction;
	problem: OrderingProblem;
	/** For `tableTaken`: the order at the chosen table. */
	takenBy: string | null;
};

/**
 * Changing an open order (OP-08, OP-10): cancel a line or some of its units
 * (the caller makes `clientRequestId` once per dialog, so a retry is the same
 * cancel), cancel the whole order, move it to a free table, or merge another
 * open order into it. Cancelling a line goes through the outbox (it works
 * offline); the others need the server now: each answers with the order,
 * which goes straight into the cache, and a refusal leaves `problem`.
 * Returns true when done (or queued).
 */
export function useOrderActions(orderId: string) {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const core = useStaffCore();
	const restaurantId = useRestaurantId();
	const [problem, setProblem] = useState<OrderActionProblem | null>(null);
	const [busy, setBusy] = useState<OrderAction | null>(null);
	const options = {
		onSuccess: (order: Order) => {
			queryClient.setQueryData(trpc.staff.ordering.getOrder.queryKey({ orderId: order.id }), order);
		},
		onSettled: () =>
			void queryClient.invalidateQueries({ queryKey: trpc.staff.ordering.pathKey() }),
	};
	const cancelOrder = useMutation(trpc.staff.ordering.cancelOrder.mutationOptions(options));
	const move = useMutation(trpc.staff.ordering.moveTable.mutationOptions(options));
	const merge = useMutation(trpc.staff.ordering.mergeOrders.mutationOptions(options));

	const run = useCallback(async (action: OrderAction, call: () => Promise<unknown>) => {
		setProblem(null);
		setBusy(action);
		try {
			await call();
			return true;
		} catch (error) {
			setProblem({ action, problem: orderingProblemOf(error), takenBy: takenByOrderOf(error) });
			return false;
		} finally {
			setBusy(null);
		}
	}, []);

	return {
		/**
		 * Goes through the outbox (P3-T29): the line shows as cancelling at
		 * once, and the cancel reaches the server when it can, with the
		 * dialog's request ID. A refusal shows on the line.
		 */
		cancelItem: async (input: CancelItemInput, itemName: string) => {
			if (!restaurantId) return false;
			core.outbox.add({
				kind: "cancelItem",
				id: input.clientRequestId,
				restaurantId,
				orderId,
				input,
				itemName,
			});
			return true;
		},
		cancelOrder: (reason: string) =>
			run("cancelOrder", () => cancelOrder.mutateAsync({ orderId, reason })),
		moveTo: (tableId: string) => run("move", () => move.mutateAsync({ orderId, tableId })),
		/** Merges `sourceOrderId` into this order; the source closes. */
		mergeIn: (sourceOrderId: string) =>
			run("merge", () => merge.mutateAsync({ sourceOrderId, targetOrderId: orderId })),
		busy,
		problem,
		clearProblem: useCallback(() => setProblem(null), []),
	};
}

/** Free tables to move an order to, and open orders to merge into it (OP-10). */
export function useOrderTargets(order: Pick<Order, "id" | "tableId"> | null) {
	const trpc = useTRPC();
	const tables = useQuery(trpc.staff.tables.list.queryOptions());
	const live = useLiveBoard();
	return useMemo(
		() => ({
			ready: Boolean(tables.data) && live.status === "ready",
			moveTargets: order && tables.data ? moveTargets(tables.data, live.board, order) : [],
			mergeCandidates: order ? mergeCandidates(live.board, order.id) : [],
		}),
		[order, tables.data, live.board, live.status],
	);
}

/**
 * A table for a new order: its label, and the open order already there (a
 * tap on a taken table opens that order instead).
 */
export function useTableForOrder(tableId: string) {
	const trpc = useTRPC();
	const tables = useQuery(trpc.staff.tables.list.queryOptions());
	const live = useLiveBoard();
	const table = tables.data?.tables.find((t) => t.id === tableId) ?? null;
	return {
		status:
			tables.data && live.status === "ready"
				? table
					? ("ready" as const)
					: ("notFound" as const)
				: tables.isError || live.status === "error"
					? ("error" as const)
					: ("loading" as const),
		table,
		openOrder: live.status === "ready" ? openOrderAt(live.board, tableId) : null,
	};
}
